import React, { useEffect, useMemo, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { Radio } from "@jugl-web/ui-components/cross-platform/Radio";
import { FormGroup } from "@jugl-web/ui-components/cross-platform/forms/FormGroup";
import { SelectTrigger } from "@jugl-web/ui-components/cross-platform/forms/SelectTrigger";
import { ResourcePickerPopover } from "@jugl-web/ui-components/web/ResourcePickerPopover";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { TOPUP_OPTIONS } from "./consts";

interface GetExtraCreditsDialogContentProps {
  onChange: (credits: number) => void;
  onRequestSwitch?: () => void;
}

export const GetExtraCreditsDialogContent: React.FC<
  GetExtraCreditsDialogContentProps
> = ({ onChange, onRequestSwitch }) => {
  const { t } = useTranslations();
  const { maxRollOverCredits } = useSubscriptionV2();
  const [selectedCredits, setSelectedCredits] = useState(
    TOPUP_OPTIONS[0].credits
  );

  useEffect(() => {
    onChange(selectedCredits);
  }, [selectedCredits, onChange]);

  const selectedOption = useMemo(
    () => TOPUP_OPTIONS.find((o) => o.credits === selectedCredits),
    [selectedCredits]
  );

  const totalPrice = selectedOption ? selectedOption.amountInCents / 100 : 0;

  const pickerItems = useMemo(
    () =>
      TOPUP_OPTIONS.map((o) => ({
        id: String(o.credits),
        value: o.credits.toLocaleString(),
      })),
    []
  );

  return (
    <div className="flex flex-col gap-14 text-left">
      <div className="font-secondary text-sm leading-[21px] text-dark-700">
        {t(
          {
            id: "cx-plans-page.get-extra-credits-dialog.description",
            defaultMessage:
              "This is <highlight>one-time purchase</highlight> of message credits. Please note that we roll over unused credits at the start of billing cycle (Up to {maxCredits} credits max)",
          },
          {
            highlight: (chunks: React.ReactNode) => (
              <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                {chunks}
              </span>
            ),
            maxCredits: maxRollOverCredits.toLocaleString(),
          }
        )}
      </div>

      {onRequestSwitch && (
        <button
          type="button"
          onClick={onRequestSwitch}
          className="-mt-10 cursor-pointer self-start border-none bg-transparent p-0 font-secondary text-sm leading-[1.4] text-[#1045A1] underline"
        >
          {t({
            id: "cx-plans-page.get-extra-credits-dialog.switch-to-auto-renewal",
            defaultMessage: "Set Credits auto-renewal",
          })}
        </button>
      )}

      <FormGroup
        label={t({
          id: "cx-plans-page.get-extra-credits-dialog.credits-amount",
          defaultMessage: "Credits amount",
        })}
        labelPosition="border-overlap"
      >
        <ResourcePickerPopover
          items={pickerItems}
          selectionBehavior={{ mode: "single", canToggle: false }}
          defaultSelectedIds={[String(selectedCredits)]}
          placement="bottom"
          onSelect={({ item, onClose }) => {
            setSelectedCredits(Number(item.id));
            onClose();
          }}
          renderLabel={({ value }) => value}
          renderEndIcon={(_item, _index, isSelected) => (
            <Radio isChecked={isSelected} readOnly />
          )}
          renderTrigger={({ Trigger, triggerRef, isOpen }) => (
            <Trigger
              ref={triggerRef}
              as={SelectTrigger}
              label={selectedCredits.toLocaleString()}
              isOpen={isOpen}
              hasValue
            />
          )}
        />
      </FormGroup>

      <div className="flex flex-col gap-6">
        <div className="h-px bg-dark-100" />
        <div className="flex items-start justify-between px-4">
          <div className="flex flex-col gap-1">
            <span className="font-secondary text-base leading-6 font-medium text-dark">
              {t({
                id: "cx-plans-page.get-extra-credits-dialog.total",
                defaultMessage: "Total",
              })}
            </span>
            <span className="font-secondary text-sm leading-[19.6px] text-dark-700">
              {t({
                id: "cx-plans-page.get-extra-credits-dialog.charged-immediately",
                defaultMessage: "Will be charged immediately",
              })}
            </span>
          </div>
          <span className="font-secondary text-lg leading-[27px] font-semibold text-dark">
            ${totalPrice}
          </span>
        </div>
      </div>
    </div>
  );
};
